// Copyright 2020 Sorama B.V.

import { bindable, computedFrom, customElement, observable } from "aurelia-framework";
import { ProgressIndicator, ProgressType } from "libraries/ProgressIndicator/ProgressIndicator";

export enum FileUploadStatus {
    Pending = "pending",
    Uploading = "uploading",
    Success = "success",
    Error = "error",
}

export class FileToUpload {
    public status: FileUploadStatus = FileUploadStatus.Pending;
    public errorMessage?: string;
    public showErrorDetails?: boolean;
    constructor(
        public name: string,
        public readonly data: Blob,
        public description: string,
        public readonly mimeType: string,
        public readonly progressCallback: (progress: ProgressIndicator) => void
    ) {}

    public dataAsBase64(): Promise<string> {
        const reader = new FileReader();

        const readerPromise = new Promise<string>((resolve, reject) => {
            reader.onloadend = () => {
                resolve(<string>reader.result);
            };

            reader.onerror = (e) => {
                reject(e);
            };
        });

        reader.readAsDataURL(this.data);

        return readerPromise;
    }
}

export class FileUploadError {
    constructor(
        public file: FileToUpload,
        public error: string
    ) {}
}

@customElement("file-dropbox")
export class FileDropbox {
    @bindable()
    public dropText: string;
    /**
     * Specify the file type accepted by this drop element. See: https://www.w3schools.com/tags/att_input_accept.asp
     */
    @bindable({})
    public fileType: string;

    /**
     * Allow multiple files to be uploaded at once
     */
    @bindable()
    public allowMultiple = false;

    @bindable
    public uploadFile: (file: FileToUpload) => Promise<void>;

    @bindable
    public onFinish: (failedImports: FileUploadError[]) => void;

    @bindable
    public onReset: () => void;

    @bindable({})
    public uploadAs2FileSizeLimit: number = undefined;

    @bindable({})
    public uploadSorFileSizeLimit: number = undefined;

    @bindable({})
    public showDescription = true;

    @observable
    private selectedFiles: FileList;

    protected dragOver: boolean;
    protected dropAreaElement: HTMLInputElement;
    protected busyLoading: boolean;

    protected errors: string[];
    protected failedFileImports: FileUploadError[];

    protected filesToUpload: FileToUpload[] = [];

    protected activeErrorFile: FileToUpload = null;

    protected filesInProgress: ProgressIndicator[] = [];
    protected filesInProgressChangeTracker = 0; // Used to trigger progress recalculation

    /**
     * Base64 preview string
     */
    protected preview: string;
    @computedFrom("fileType")
    protected get isMeasurementType(): boolean {
        return this.fileType.search(".as2, .sor, .json") >= 0;
    }

    @computedFrom("fileType")
    protected get isCustomMeasurementType(): boolean {
        return this.fileType.search(".as2, .sor") >= 0 && this.fileType.search(".json") < 0;
    }

    @computedFrom("fileType")
    protected get isSpecialMeasurementType(): boolean {
        return this.fileType.search(".json") >= 0 && this.fileType.search(".as2, .sor") < 0;
    }

    @computedFrom("fileType")
    protected get isImageType(): boolean {
        return this.fileType.search("image/") >= 0;
    }

    @computedFrom("fileType")
    protected get isVideoType(): boolean {
        return this.fileType.search("video/") >= 0;
    }

    @computedFrom("fileType", "allowMultiple", "dropText")
    protected get formatString(): string {
        if (this.dropText) {
            return this.dropText;
        }

        switch (true) {
            case this.isImageType:
                return "image";
            case this.isVideoType:
                return "video";
            case this.isCustomMeasurementType:
                return "AS2 or SOR";
            case this.isSpecialMeasurementType:
                return "file (in JSON format)";
            case this.isMeasurementType:
                return "AS2, SOR or JSON file" + (this.allowMultiple ? "s" : "");
            default:
                return "file";
        }
    }

    @computedFrom("isVideoType", "newImage")
    protected get videoElementSource(): string {
        if (this.isVideoType && this.preview) {
            return `<video><source src="${this.preview}" + type="video/mp4"></source></video>`;
        }
        return "";
    }

    @computedFrom("filesToUpload")
    public get allFilesNotPending(): boolean {
        return this.filesToUpload?.length > 0 && this.filesToUpload.every((f) => f.status !== "pending");
    }

    public attached(): void {
        this.dropAreaElement.addEventListener("dragenter", this.onDragEnter, false);
        this.dropAreaElement.addEventListener("dragleave", this.onDragLeave, false);
        this.dropAreaElement.addEventListener("dragover", this.onDragEnter, false);
        this.dropAreaElement.addEventListener("drop", this.onDrop, false);
    }

    public detached(): void {
        this.dropAreaElement.removeEventListener("dragenter", this.onDragEnter, false);
        this.dropAreaElement.removeEventListener("dragleave", this.onDragLeave, false);
        this.dropAreaElement.removeEventListener("dragover", this.onDragEnter, false);
        this.dropAreaElement.removeEventListener("drop", this.onDrop, false);
    }

    protected upload(): Promise<void> {
        if (!this.filesToUpload?.length || !this.uploadFile) {
            return Promise.resolve();
        }

        this.errors = this.errors || [];
        this.failedFileImports = this.failedFileImports || [];

        // accumulate results locally to avoid race condition
        const newErrors: string[] = [];
        const newFailedFileImports: FileUploadError[] = [];

        const uploadTasks = this.filesToUpload.map(async (file) => {
            if (file.status === FileUploadStatus.Success) return;

            file.status = FileUploadStatus.Uploading;

            try {
                const result = this.uploadFile(file);
                await Promise.resolve(result); // normalize non-promise returns
                if (file.status === FileUploadStatus.Uploading) {
                    if (file.errorMessage) {
                        file.status = FileUploadStatus.Error;
                        if (this.allowMultiple) {
                            newFailedFileImports.push(new FileUploadError(file, file.errorMessage));
                        } else {
                            newErrors.push(file.errorMessage);
                        }
                    } else {
                        file.status = FileUploadStatus.Success;
                    }
                }
            } catch (e) {
                const message = (e && e.message) || "Unknown error";
                file.status = FileUploadStatus.Error;
                file.errorMessage = message;

                if (this.allowMultiple) {
                    newFailedFileImports.push(new FileUploadError(file, message));
                } else {
                    newErrors.push(message);
                }
            }
        });

        return Promise.allSettled(uploadTasks).then(() => {
            this.errors.push(...newErrors);
            this.failedFileImports.push(...newFailedFileImports);

            this.filesToUpload = this.filesToUpload.slice();
            if (this.onFinish) {
                this.onFinish(this.failedFileImports);
            }
        });
    }

    // Clears all files from the FileUploader queue
    protected emptyFileQueue() {
        this.filesInProgress = [];
        this.filesInProgressChangeTracker = 0;
        this.filesToUpload = [];

        // Reset the selectedFiles by the dropAreaElement ref. (modifying selectedFiles does not work)
        this.dropAreaElement.files = undefined;
        this.dropAreaElement.value = "";
    }

    public cancelAction(): void {
        this.emptyFileQueue();

        // Clear errors
        this.errors = [];
        this.failedFileImports = [];

        if (this.onReset) this.onReset();
    }

    protected selectedFilesChanged(): void {
        if (!this.selectedFiles) {
            return;
        }

        const files = this.selectedFiles.length > 0 ? this.selectedFiles : undefined;

        if (!files) {
            return;
        }

        this.openFiles(files);
    }

    private onDragEnter = (e): void => {
        e.stopPropagation();
        e.preventDefault();

        this.dragOver = true;
    };

    private onDragLeave = (e): void => {
        e.stopPropagation();
        e.preventDefault();

        this.dragOver = false;
    };

    private onDrop = (e: DragEvent): void => {
        e.stopPropagation();
        e.preventDefault();

        this.dragOver = false;

        // Upload file
        const files = e.dataTransfer.files && e.dataTransfer.files.length > 0 ? e.dataTransfer.files : undefined;

        if (!files) {
            return;
        }

        this.openFiles(files);
    };

    private static formatBytes(bytes, decimals = 2) {
        if (bytes === 0) return "0 Bytes";

        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    }

    private openFiles(files: FileList): void {
        if (this.onReset) this.onReset();

        this.errors = [];
        this.failedFileImports = [];

        const tooLargeFiles = [];
        for (const file of files) {
            const uploadFileSizeLimit = this.getUploadFileSizeLimit(file);

            if (uploadFileSizeLimit && file.size > uploadFileSizeLimit) {
                tooLargeFiles.push({ file: file, limit: uploadFileSizeLimit });
            }

            const newProgressIndex = this.filesInProgress.push(undefined);
            this.filesToUpload.push(
                new FileToUpload(file.name, file.slice(), "", file.type, (p) =>
                    this.progressCallback(p, newProgressIndex - 1)
                )
            );
        }

        if (tooLargeFiles.length) {
            this.errors.push(`Maximum file size exceeded for ${tooLargeFiles.length} file(s):`);
            tooLargeFiles.forEach((f) => this.errors.push(`${f.file.name} (max ${FileDropbox.formatBytes(f.limit)})`));
        }

        if (!this.allowMultiple && this.filesToUpload?.length > 0) {
            this.filesToUpload[0].dataAsBase64().then((x) => (this.preview = x));
        }
    }

    protected showErrorPopup(file: FileToUpload): void {
        this.activeErrorFile = file;
    }

    protected closeErrorPopup(): void {
        this.activeErrorFile = null;
    }

    private getUploadFileSizeLimit(fileToUpload: File): number {
        let uploadFileSizeLimit: number = undefined;
        try {
            const fileExt = fileToUpload.name.split(".").pop();
            if (fileExt === "sor") {
                uploadFileSizeLimit = this.uploadSorFileSizeLimit;
            } else if (fileExt === "as2") {
                uploadFileSizeLimit = this.uploadAs2FileSizeLimit;
            }
        } catch (e) {
            // Ignore errors
        }

        return uploadFileSizeLimit;
    }

    private progressCallback = (progress: ProgressIndicator, index: number) => {
        this.filesInProgress[index] = progress;
        this.filesInProgressChangeTracker = (this.filesInProgressChangeTracker + 1) % 1e5; // Trigger progress text and percentage recalculation
    };

    @computedFrom("filesInProgress", "filesInProgressChangeTracker")
    protected get progressText(): string {
        // - Show "Uploading" while at least one file is uploading
        // - Show "Downloading" while at least one file is downloading
        // - Show "Processing" otherwise, if something is still in progress

        if (this.filesInProgress.length === 0) {
            return "";
        }

        if (
            this.filesInProgress.some(
                (progress) =>
                    progress !== undefined &&
                    progress.progresType === ProgressType.Upload &&
                    progress.progressEvent.loaded < progress.progressEvent.total
            )
        ) {
            return "Uploading";
        }

        if (
            this.filesInProgress.some(
                (progress) => progress !== undefined && progress.progresType === ProgressType.Download
            )
        ) {
            return "Downloading";
        }

        if (
            this.filesInProgress.some(
                (progress) =>
                    progress !== undefined &&
                    (progress.progresType === ProgressType.Processing ||
                        progress.progressEvent.loaded >= progress.progressEvent.total)
            )
        ) {
            return "Processing";
        }

        return "";
    }

    @computedFrom("filesProgress", "filesInProgressChangeTracker")
    protected get progressPercentage(): number {
        // - Show total upload progress while at least one file is uploading
        // - Otherwise, show total download progress while at least one file is downloading
        // - Otherwise, show no percentage

        let activeProgressType = ProgressType.Processing;

        const activeProgressFiles = this.filesInProgress.filter((p) => p);

        if (
            activeProgressFiles.some(
                (progress) =>
                    progress.progresType === ProgressType.Upload &&
                    progress.progressEvent.loaded < progress.progressEvent.total
            )
        ) {
            activeProgressType = ProgressType.Upload;
        } else if (activeProgressFiles.some((progress) => progress.progresType === ProgressType.Download)) {
            activeProgressType = ProgressType.Download;
        } else {
            return undefined;
        }

        const totalLoaded = activeProgressFiles.reduce((partialSum: number, p) => {
            let loadValue = 0;

            if (activeProgressType === ProgressType.Upload) {
                // While uploading, files that are uploading count towards the loaded value normally,
                // downloaded or processing files are already 'done'
                loadValue = p.progresType === ProgressType.Upload ? p.progressEvent.loaded : p.progressEvent.total;
            } else if (activeProgressType === ProgressType.Download) {
                // While downloading, only files that are downloading count towards the loaded value
                loadValue = p.progresType === ProgressType.Download ? p.progressEvent.loaded : 0;
            }

            return partialSum + loadValue;
        }, 0);

        const totalToLoad = activeProgressFiles.reduce(
            (partialSum: number, p) => partialSum + p.progressEvent.total,
            0
        );

        return Math.round((totalLoaded / totalToLoad) * 100);
    }
}
